/*
 * Luxe & Gloss cart service, contract version 1.
 * Owns all Ajax Cart API transport; UI code consumes window.LG.cart.
 */
(function () {
  'use strict'

  window.LG = window.LG || {}

  if (window.LG.cart && window.LG.cart.version === 1) return

  const DEFAULT_MESSAGE = 'We could not update your bag. Please try again.'
  const AMBIGUOUS_MESSAGE = 'We could not confirm whether your bag changed. Review your bag before trying again.'
  const SECTION_IDS = ['cart-count-badge', 'offcanvas-cart', 'lg-cart-main']
  const REQUEST_TIMEOUT_MS = 15000
  const inflightAdds = new Map()
  let writeQueue = Promise.resolve()
  let lastConfirmedCart = null
  let lastTrigger = null
  let requestSequence = 0

  class CartServiceError extends Error {
    constructor (options) {
      super(options.message || DEFAULT_MESSAGE)
      this.name = 'CartServiceError'
      this.version = 1
      this.code = options.code || 'cart_error'
      this.action = options.action || 'unknown'
      this.requestId = options.requestId || null
      this.source = options.source || 'unknown'
      this.recoverable = Boolean(options.recoverable)
      this.safeToRetry = Boolean(options.safeToRetry)
      this.needsReconciliation = Boolean(options.needsReconciliation)
      this.status = Number.isFinite(options.status) ? options.status : null
      this.cart = options.cart || null
    }

    toJSON () {
      return {
        version: this.version,
        code: this.code,
        message: this.message,
        action: this.action,
        requestId: this.requestId,
        source: this.source,
        recoverable: this.recoverable,
        safeToRetry: this.safeToRetry,
        needsReconciliation: this.needsReconciliation,
        status: this.status,
        cart: this.cart
      }
    }
  }

  function nextRequestId () {
    requestSequence += 1
    const randomPart = window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : Math.random().toString(36).slice(2)

    return `lg-cart-${Date.now()}-${requestSequence}-${randomPart}`
  }

  function endpoint (routeName, fallbackPath) {
    const routes = window.Shopify && window.Shopify.routes ? window.Shopify.routes : {}
    const route = routes[routeName]

    if (typeof route === 'string' && route.length) {
      return route.endsWith('.js') ? route : `${route}.js`
    }

    const root = typeof routes.root === 'string' && routes.root.length ? routes.root : '/'
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`
    return `${normalizedRoot}${fallbackPath}.js`
  }

  function readableMessage (value, fallback) {
    if (typeof value !== 'string') return fallback

    const container = document.createElement('div')
    container.innerHTML = value
    const text = (container.textContent || '').replace(/\s+/g, ' ').trim()
    return text || fallback
  }

  async function parseResponse (response) {
    const text = await response.text()

    if (!text) return null

    try {
      return JSON.parse(text)
    } catch (error) {
      throw new CartServiceError({
        code: 'invalid_json',
        message: 'The store returned an unreadable response.',
        status: response.status,
        recoverable: false,
        safeToRetry: false,
        needsReconciliation: response.ok || response.status >= 500
      })
    }
  }

  async function requestJson (url, options, context) {
    let response
    const controller = window.AbortController ? new window.AbortController() : null
    const timeoutId = controller
      ? window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
      : null

    try {
      response = await window.fetch(url, {
        credentials: 'same-origin',
        ...options,
        ...(controller ? { signal: controller.signal } : {}),
        headers: {
          Accept: 'application/json',
          ...(options && options.headers ? options.headers : {})
        }
      })
    } catch (error) {
      const timedOut = error && error.name === 'AbortError'
      throw new CartServiceError({
        ...context,
        code: timedOut ? 'timeout' : 'network_error',
        message: timedOut ? 'The store took too long to respond.' : 'Check your connection, then try again.',
        recoverable: true,
        safeToRetry: context.action !== 'add',
        needsReconciliation: context.action === 'add' || context.action === 'change' || context.action === 'update'
      })
    } finally {
      if (timeoutId !== null) window.clearTimeout(timeoutId)
    }

    let data

    try {
      data = await parseResponse(response)
    } catch (error) {
      if (error instanceof CartServiceError) {
        error.action = context.action
        error.requestId = context.requestId
        error.source = context.source
        if (context.action === 'get') {
          error.recoverable = true
          error.safeToRetry = true
          error.needsReconciliation = false
        }
        throw error
      }

      throw new CartServiceError({
        ...context,
        code: 'network_error',
        message: 'The store response was interrupted. Check your bag before trying again.',
        status: response.status,
        recoverable: true,
        safeToRetry: context.action !== 'add',
        needsReconciliation: context.action === 'add' || context.action === 'change' || context.action === 'update'
      })
    }

    if (!response.ok) {
      const rejected = response.status >= 400 && response.status < 500 && response.status !== 408 && response.status !== 429
      const fallback = rejected
        ? 'That change is not available. Review your selection and try again.'
        : DEFAULT_MESSAGE

      throw new CartServiceError({
        ...context,
        code: rejected ? 'cart_rejected' : (response.status === 429 ? 'rate_limited' : 'server_error'),
        message: readableMessage(data && (data.description || data.message), fallback),
        status: response.status,
        recoverable: true,
        safeToRetry: rejected,
        needsReconciliation: !rejected
      })
    }

    return data
  }

  async function fetchCart (context, attempts) {
    let lastError
    const totalAttempts = attempts || 1

    for (let attempt = 0; attempt < totalAttempts; attempt += 1) {
      try {
        const cart = await requestJson(endpoint('cart_url', 'cart'), { method: 'GET' }, context)

        if (!cart || !Array.isArray(cart.items) || !Number.isFinite(cart.item_count)) {
          throw new CartServiceError({
            ...context,
            code: 'invalid_cart',
            message: 'The store returned an incomplete bag.',
            recoverable: true,
            safeToRetry: true
          })
        }

        lastConfirmedCart = cart
        return cart
      } catch (error) {
        lastError = error
      }
    }

    throw lastError
  }

  function normalizeProperties (properties) {
    if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return {}

    return Object.keys(properties).reduce((result, key) => {
      const value = properties[key]
      if (value !== undefined && value !== null && String(value).length) result[key] = String(value)
      return result
    }, {})
  }

  function normalizeAddItems (items) {
    if (!Array.isArray(items) || !items.length) {
      throw new TypeError('LG.cart.add requires a non-empty items array.')
    }

    return items.map(item => {
      const quantity = Number(item && item.quantity === undefined ? 1 : item.quantity)
      const id = item && item.id

      if ((typeof id !== 'string' && typeof id !== 'number') || !String(id).length) {
        throw new TypeError('Each cart item requires a variant id.')
      }

      if (!Number.isInteger(quantity) || quantity < 1) {
        throw new TypeError('Each cart item quantity must be a positive integer.')
      }

      const normalized = {
        id,
        quantity,
        properties: normalizeProperties(item.properties)
      }

      if (item.selling_plan !== undefined && item.selling_plan !== null && String(item.selling_plan).length) {
        normalized.selling_plan = item.selling_plan
      }

      return normalized
    })
  }

  function stableValue (value) {
    if (Array.isArray(value)) return `[${value.map(stableValue).join(',')}]`
    if (value && typeof value === 'object') {
      return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableValue(value[key])}`).join(',')}}`
    }
    return JSON.stringify(value)
  }

  function lineIdentity (item) {
    const id = item.variant_id === undefined ? item.id : item.variant_id
    const properties = normalizeProperties(item.properties)
    const allocation = item.selling_plan_allocation
    const sellingPlan = item.selling_plan === undefined
      ? (allocation && allocation.selling_plan ? allocation.selling_plan.id : null)
      : item.selling_plan

    return stableValue({ id: String(id), properties, sellingPlan: sellingPlan === null ? null : String(sellingPlan) })
  }

  function quantitiesByIdentity (items) {
    return items.reduce((quantities, item) => {
      const identity = lineIdentity(item)
      quantities.set(identity, (quantities.get(identity) || 0) + Number(item.quantity || 0))
      return quantities
    }, new Map())
  }

  function addWasApplied (before, after, requestedItems) {
    const beforeQuantities = quantitiesByIdentity(before.items)
    const afterQuantities = quantitiesByIdentity(after.items)
    const requestedQuantities = quantitiesByIdentity(requestedItems)

    return [...requestedQuantities.entries()].every(([identity, requestedQuantity]) => {
      const beforeQuantity = beforeQuantities.get(identity) || 0
      const afterQuantity = afterQuantities.get(identity) || 0
      return afterQuantity - beforeQuantity >= requestedQuantity
    })
  }

  function changeWasApplied (cart, key, quantity) {
    const line = cart.items.find(item => item.key === key)
    return quantity === 0 ? !line : Boolean(line && Number(line.quantity) === quantity)
  }

  function dispatch (name, detail) {
    window.dispatchEvent(new CustomEvent(name, { detail }))
  }

  function emitSections (sections, context) {
    if (!sections || typeof sections !== 'object') return

    const validSections = Object.keys(sections).reduce((result, key) => {
      if (typeof sections[key] === 'string') result[key] = sections[key]
      return result
    }, {})

    if (!Object.keys(validSections).length) return

    dispatch('lg:cart:sections', {
      version: 1,
      requestId: context.requestId,
      source: context.source,
      action: context.action,
      sections: validSections
    })
  }

  function emitUpdated (cart, context, sections, legacyData) {
    emitSections(sections, context)
    dispatch('lg:cart:updated', {
      version: 1,
      requestId: context.requestId,
      source: context.source,
      action: context.action,
      cart
    })

    if (context.action === 'add') dispatch('kt.cart.added', legacyData || cart)
    if (context.action === 'change' || context.action === 'update') dispatch('kt.cart.changed', legacyData || cart)
    if (sections) dispatch('kt.cart.reloaded', { cart })
  }

  function emitError (error) {
    const normalized = error instanceof CartServiceError
      ? error
      : new CartServiceError({ code: 'cart_error', message: DEFAULT_MESSAGE })

    dispatch('lg:cart:error', normalized.toJSON())
    return normalized
  }

  function enqueueWrite (operation) {
    const pending = writeQueue.then(operation, operation)
    writeQueue = pending.catch(() => {})
    return pending
  }

  function sectionsPayload () {
    return {
      sections: SECTION_IDS,
      sections_url: window.location.pathname
    }
  }

  async function reconcileAdd (before, items, context, originalError) {
    let after

    try {
      after = await fetchCart(context, 2)
    } catch (reconciliationError) {
      throw new CartServiceError({
        ...context,
        code: 'ambiguous_add',
        message: AMBIGUOUS_MESSAGE,
        status: originalError.status,
        recoverable: false,
        safeToRetry: false,
        needsReconciliation: true
      })
    }

    if (addWasApplied(before, after, items)) {
      emitUpdated(after, context)
      return after
    }

    throw new CartServiceError({
      ...context,
      code: originalError.code,
      message: originalError.message,
      status: originalError.status,
      cart: after,
      recoverable: true,
      safeToRetry: true,
      needsReconciliation: false
    })
  }

  async function performAdd (items, source, requestId) {
    const context = { action: 'add', source, requestId }
    let before

    try {
      before = await fetchCart(context, 2)
    } catch (error) {
      throw new CartServiceError({
        ...context,
        code: 'preflight_failed',
        message: 'We could not check your bag. Please try again.',
        status: error.status,
        recoverable: true,
        safeToRetry: true
      })
    }

    let responseData

    try {
      responseData = await requestJson(endpoint('cart_add_url', 'cart/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, ...sectionsPayload() })
      }, context)
    } catch (error) {
      if (error.needsReconciliation) return reconcileAdd(before, items, context, error)
      throw error
    }

    let cart

    try {
      cart = await fetchCart(context, 2)
    } catch (error) {
      return reconcileAdd(before, items, context, error)
    }

    if (!addWasApplied(before, cart, items)) {
      throw new CartServiceError({
        ...context,
        code: 'state_mismatch',
        message: 'The selected item was not confirmed in your bag. Please try again.',
        cart,
        recoverable: true,
        safeToRetry: true
      })
    }

    emitUpdated(cart, context, responseData && responseData.sections, responseData)
    return cart
  }

  function add (options) {
    const source = options && options.source ? String(options.source) : 'unknown'
    const requestId = nextRequestId()
    let items

    try {
      items = normalizeAddItems(options && options.items)
    } catch (error) {
      const structuredError = emitError(new CartServiceError({
        action: 'add',
        source,
        requestId,
        code: 'invalid_request',
        message: error.message,
        recoverable: true,
        safeToRetry: true
      }))
      return Promise.reject(structuredError)
    }

    const fingerprint = stableValue({ source, items })
    const existing = inflightAdds.get(fingerprint)
    if (existing) return existing

    const pending = enqueueWrite(() => performAdd(items, source, requestId))
      .catch(error => { throw emitError(error) })

    inflightAdds.set(fingerprint, pending)
    pending.then(
      () => inflightAdds.delete(fingerprint),
      () => inflightAdds.delete(fingerprint)
    )

    return pending
  }

  function change (options) {
    const source = options && options.source ? String(options.source) : 'cart'
    const requestId = nextRequestId()
    const key = options && options.key
    const rawQuantity = options && options.quantity
    const hasQuantity = rawQuantity !== undefined && rawQuantity !== null && String(rawQuantity).trim().length > 0
    const quantity = Number(rawQuantity)
    const context = { action: 'change', source, requestId }

    if (typeof key !== 'string' || !key.length || !hasQuantity || !Number.isInteger(quantity) || quantity < 0) {
      const error = emitError(new CartServiceError({
        ...context,
        code: 'invalid_request',
        message: 'Choose a valid bag item and quantity.',
        recoverable: true,
        safeToRetry: true
      }))
      return Promise.reject(error)
    }

    return enqueueWrite(async () => {
      const before = await fetchCart(context, 2)

      if (!before.items.some(item => item.key === key)) {
        throw new CartServiceError({
          ...context,
          code: 'stale_line',
          message: 'That bag item changed. Refresh your bag and try again.',
          cart: before,
          recoverable: true,
          safeToRetry: true
        })
      }

      let responseData

      try {
        responseData = await requestJson(endpoint('cart_change_url', 'cart/change'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity, ...sectionsPayload() })
        }, context)
      } catch (error) {
        if (!error.needsReconciliation) {
          error.cart = error.cart || before
          throw error
        }

        let reconciledCart

        try {
          reconciledCart = await fetchCart(context, 2)
        } catch {
          throw new CartServiceError({
            ...context,
            code: 'ambiguous_change',
            message: AMBIGUOUS_MESSAGE,
            status: error.status,
            cart: before,
            recoverable: false,
            safeToRetry: false,
            needsReconciliation: true
          })
        }

        if (!changeWasApplied(reconciledCart, key, quantity)) {
          throw new CartServiceError({
            ...context,
            code: error.code,
            message: error.message,
            status: error.status,
            cart: reconciledCart,
            recoverable: true,
            safeToRetry: true
          })
        }

        emitUpdated(reconciledCart, context)
        return reconciledCart
      }

      let cart

      try {
        cart = await fetchCart(context, 2)
      } catch (confirmationError) {
        throw new CartServiceError({
          ...context,
          code: 'ambiguous_change',
          message: AMBIGUOUS_MESSAGE,
          status: confirmationError.status,
          cart: before,
          recoverable: false,
          safeToRetry: false,
          needsReconciliation: true
        })
      }

      if (!changeWasApplied(cart, key, quantity)) {
        throw new CartServiceError({
          ...context,
          code: 'state_mismatch',
          message: 'Your quantity was not confirmed. Refresh your bag and try again.',
          cart,
          recoverable: true,
          safeToRetry: true
        })
      }

      emitUpdated(cart, context, responseData && responseData.sections, responseData)
      return cart
    }).catch(error => { throw emitError(error) })
  }

  function update (options) {
    const source = options && options.source ? String(options.source) : 'cart-note'
    const requestId = nextRequestId()
    const context = { action: 'update', source, requestId }
    const note = options && options.note !== undefined ? String(options.note) : ''

    return enqueueWrite(async () => {
      let responseData

      try {
        responseData = await requestJson(endpoint('cart_update_url', 'cart/update'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note, ...sectionsPayload() })
        }, context)
      } catch (error) {
        if (!error.needsReconciliation) throw error

        const reconciledCart = await fetchCart(context, 2)
        if (String(reconciledCart.note || '') !== note) {
          throw new CartServiceError({
            ...context,
            code: error.code,
            message: error.message,
            status: error.status,
            cart: reconciledCart,
            recoverable: true,
            safeToRetry: true
          })
        }

        emitUpdated(reconciledCart, context)
        return reconciledCart
      }

      const cart = await fetchCart(context, 2)
      if (String(cart.note || '') !== note) {
        throw new CartServiceError({
          ...context,
          code: 'state_mismatch',
          message: 'Your bag note was not confirmed. Please try again.',
          cart,
          recoverable: true,
          safeToRetry: true
        })
      }

      emitUpdated(cart, context, responseData && responseData.sections, responseData)
      return cart
    }).catch(error => { throw emitError(error) })
  }

  function get () {
    const context = { action: 'get', source: 'cart', requestId: nextRequestId() }
    return writeQueue.then(() => fetchCart(context, 2)).catch(error => { throw emitError(error) })
  }

  async function open (options) {
    const trigger = options && options.trigger
    if (trigger && typeof trigger.focus === 'function') lastTrigger = trigger
    else if (document.activeElement && typeof document.activeElement.focus === 'function') lastTrigger = document.activeElement

    const drawer = document.querySelector('[data-lg-cart-drawer]')
    if (!drawer) {
      window.location.assign(endpoint('cart_url', 'cart').replace(/\.js$/, ''))
      return lastConfirmedCart
    }

    dispatch('lg:cart:open', { version: 1, trigger: lastTrigger })

    if (window.bootstrap && window.bootstrap.Offcanvas) {
      window.bootstrap.Offcanvas.getOrCreateInstance(drawer).show()
    } else {
      drawer.removeAttribute('hidden')
      drawer.classList.add('show')
      drawer.setAttribute('aria-modal', 'true')
      drawer.removeAttribute('aria-hidden')
      document.body.classList.add('lg-cart-drawer-open')
      const closeButton = drawer.querySelector('[data-lg-cart-close]')
      if (closeButton) closeButton.focus()
    }

    return lastConfirmedCart || get()
  }

  function getLastTrigger () {
    return lastTrigger
  }

  window.LG.cart = Object.freeze({
    version: 1,
    Error: CartServiceError,
    add,
    change,
    get,
    open,
    update,
    getLastTrigger
  })
}())
